/**
 * Centralized Icon component library
 * All icons from Heroicons (outline style) for consistency
 *
 * Usage:
 *   import { Icon } from './components/ui/Icon'
 *   <Icon name="chevronLeft" className="w-5 h-5" />
 *
 * Or import individual icons:
 *   import { ChevronLeftIcon, UserIcon } from './components/ui/Icon'
 */

// Icon path definitions (Heroicons outline)
const iconPaths = {
  // Navigation
  chevronLeft: 'M15 19l-7-7 7-7',
  chevronRight: 'M9 5l7 7-7 7',
  chevronDown: 'M19 9l-7 7-7-7',
  chevronUp: 'M5 15l7-7 7 7',
  arrowLeft: 'M10 19l-7-7m0 0l7-7m-7 7h18',
  arrowRight: 'M14 5l7 7m0 0l-7 7m7-7H3',
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  menu: 'M4 6h16M4 12h16M4 18h16',

  // Actions
  close: 'M6 18L18 6M6 6l12 12',
  plus: 'M12 4v16m8-8H4',
  minus: 'M20 12H4',
  check: 'M5 13l4 4L19 7',
  edit: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  trash: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  copy: 'M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  filter: 'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z',
  download: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
  upload: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12',
  share: 'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z',
  save: 'M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4',

  // Users & People
  user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  userPlus: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  userCircle: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z',

  // Communication
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  chatAlt: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
  mail: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  phone: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  bell: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',

  // Media
  photo: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  camera: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z',
  microphone: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z',
  volumeUp: 'M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z',
  play: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  pause: 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z',
  stop: 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z',

  // Documents
  document: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  documentText: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  clipboard: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  clipboardList: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  folder: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',

  // Data & Charts
  chartBar: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  trendingUp: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  trendingDown: 'M13 17h8m0 0V9m0 8l-8-8-4 4-6-6',
  presentationChart: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z',

  // Time & Calendar
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',

  // Status & Alerts
  checkCircle: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  exclamationCircle: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  exclamationTriangle: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  informationCircle: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  xCircle: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',

  // Objects
  heart: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  heartFilled: 'M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z',
  star: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
  bookmark: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z',
  bookmarkFilled: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z',
  lightning: 'M13 10V3L4 14h7v7l9-11h-7z',
  sparkles: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
  fire: 'M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z',
  lightbulb: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  link: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  externalLink: 'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14',
  cog: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  adjustments: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',

  // Fitness specific
  dumbbell: 'M4 8h2m0 0v8m0-8h12m0 0v8m0-8h2m-2 0H6m0 8h2m0 0H6m10 0h2m0 0h-2M6 8h12v8H6V8z',
  scale: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
  target: 'M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-10c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z',

  // Misc
  dots: 'M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z',
  dotsVertical: 'M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z',
  eye: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  eyeOff: 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21',
  wifi: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0',
  wifiOff: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01M3 3l18 18M4.929 4.929A10.96 10.96 0 0112 3c5.392 0 9.878 3.88 10.819 9M1.394 9.393c1.075-1.076 2.292-1.966 3.606-2.647M10.395 10.395a6 6 0 016.61 1.01',
  paperAirplane: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  attachment: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
  send: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
}

// Base Icon component
export function Icon({
  name,
  className = 'w-5 h-5',
  strokeWidth = 2,
  fill = 'none',
  stroke = 'currentColor',
  ...props
}) {
  const path = iconPaths[name]

  if (!path) {
    console.warn(`Icon "${name}" not found`)
    return null
  }

  // Some icons have multiple paths (separated by space between M commands)
  const paths = path.split(' M').map((p, i) => (i === 0 ? p : 'M' + p))

  return (
    <svg
      className={className}
      fill={fill}
      stroke={stroke}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...props}
    >
      {paths.map((d, i) => (
        <path
          key={i}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          d={d}
        />
      ))}
    </svg>
  )
}

// Named icon exports for convenience
export const ChevronLeftIcon = (props) => <Icon name="chevronLeft" {...props} />
export const ChevronRightIcon = (props) => <Icon name="chevronRight" {...props} />
export const ChevronDownIcon = (props) => <Icon name="chevronDown" {...props} />
export const ChevronUpIcon = (props) => <Icon name="chevronUp" {...props} />
export const ArrowLeftIcon = (props) => <Icon name="arrowLeft" {...props} />
export const ArrowRightIcon = (props) => <Icon name="arrowRight" {...props} />
export const HomeIcon = (props) => <Icon name="home" {...props} />
export const MenuIcon = (props) => <Icon name="menu" {...props} />
export const CloseIcon = (props) => <Icon name="close" {...props} />
export const PlusIcon = (props) => <Icon name="plus" {...props} />
export const MinusIcon = (props) => <Icon name="minus" {...props} />
export const CheckIcon = (props) => <Icon name="check" {...props} />
export const EditIcon = (props) => <Icon name="edit" {...props} />
export const TrashIcon = (props) => <Icon name="trash" {...props} />
export const CopyIcon = (props) => <Icon name="copy" {...props} />
export const RefreshIcon = (props) => <Icon name="refresh" {...props} />
export const SearchIcon = (props) => <Icon name="search" {...props} />
export const FilterIcon = (props) => <Icon name="filter" {...props} />
export const DownloadIcon = (props) => <Icon name="download" {...props} />
export const UploadIcon = (props) => <Icon name="upload" {...props} />
export const ShareIcon = (props) => <Icon name="share" {...props} />
export const SaveIcon = (props) => <Icon name="save" {...props} />
export const UserIcon = (props) => <Icon name="user" {...props} />
export const UsersIcon = (props) => <Icon name="users" {...props} />
export const UserPlusIcon = (props) => <Icon name="userPlus" {...props} />
export const UserCircleIcon = (props) => <Icon name="userCircle" {...props} />
export const ChatIcon = (props) => <Icon name="chat" {...props} />
export const ChatAltIcon = (props) => <Icon name="chatAlt" {...props} />
export const MailIcon = (props) => <Icon name="mail" {...props} />
export const PhoneIcon = (props) => <Icon name="phone" {...props} />
export const BellIcon = (props) => <Icon name="bell" {...props} />
export const PhotoIcon = (props) => <Icon name="photo" {...props} />
export const CameraIcon = (props) => <Icon name="camera" {...props} />
export const MicrophoneIcon = (props) => <Icon name="microphone" {...props} />
export const VolumeUpIcon = (props) => <Icon name="volumeUp" {...props} />
export const PlayIcon = (props) => <Icon name="play" {...props} />
export const PauseIcon = (props) => <Icon name="pause" {...props} />
export const StopIcon = (props) => <Icon name="stop" {...props} />
export const DocumentIcon = (props) => <Icon name="document" {...props} />
export const DocumentTextIcon = (props) => <Icon name="documentText" {...props} />
export const ClipboardIcon = (props) => <Icon name="clipboard" {...props} />
export const ClipboardListIcon = (props) => <Icon name="clipboardList" {...props} />
export const FolderIcon = (props) => <Icon name="folder" {...props} />
export const ChartBarIcon = (props) => <Icon name="chartBar" {...props} />
export const TrendingUpIcon = (props) => <Icon name="trendingUp" {...props} />
export const TrendingDownIcon = (props) => <Icon name="trendingDown" {...props} />
export const CalendarIcon = (props) => <Icon name="calendar" {...props} />
export const ClockIcon = (props) => <Icon name="clock" {...props} />
export const CheckCircleIcon = (props) => <Icon name="checkCircle" {...props} />
export const ExclamationCircleIcon = (props) => <Icon name="exclamationCircle" {...props} />
export const ExclamationTriangleIcon = (props) => <Icon name="exclamationTriangle" {...props} />
export const InformationCircleIcon = (props) => <Icon name="informationCircle" {...props} />
export const XCircleIcon = (props) => <Icon name="xCircle" {...props} />
export const HeartIcon = (props) => <Icon name="heart" {...props} />
export const StarIcon = (props) => <Icon name="star" {...props} />
export const BookmarkIcon = (props) => <Icon name="bookmark" {...props} />
export const LightningIcon = (props) => <Icon name="lightning" {...props} />
export const SparklesIcon = (props) => <Icon name="sparkles" {...props} />
export const FireIcon = (props) => <Icon name="fire" {...props} />
export const LightbulbIcon = (props) => <Icon name="lightbulb" {...props} />
export const LinkIcon = (props) => <Icon name="link" {...props} />
export const ExternalLinkIcon = (props) => <Icon name="externalLink" {...props} />
export const CogIcon = (props) => <Icon name="cog" {...props} />
export const AdjustmentsIcon = (props) => <Icon name="adjustments" {...props} />
export const DotsIcon = (props) => <Icon name="dots" {...props} />
export const DotsVerticalIcon = (props) => <Icon name="dotsVertical" {...props} />
export const EyeIcon = (props) => <Icon name="eye" {...props} />
export const EyeOffIcon = (props) => <Icon name="eyeOff" {...props} />
export const WifiIcon = (props) => <Icon name="wifi" {...props} />
export const WifiOffIcon = (props) => <Icon name="wifiOff" {...props} />
export const PaperAirplaneIcon = (props) => <Icon name="paperAirplane" {...props} />
export const AttachmentIcon = (props) => <Icon name="attachment" {...props} />
export const SendIcon = (props) => <Icon name="send" {...props} />

// Export all icon names for reference
export const iconNames = Object.keys(iconPaths)

export default Icon
